"use client";

import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, Shield, ExternalLink } from "lucide-react";
import { Button } from "../ui/Button";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "service" | "privacy";
}

export function TermsModal({ isOpen, onClose, type }: TermsModalProps) {
  const isService = type === "service";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-xl bg-white/20 p-2.5">
                    {isService ? (
                      <FileText className="h-6 w-6 text-white" />
                    ) : (
                      <Shield className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {isService ? "서비스 이용약관" : "개인정보처리방침"}
                    </h2>
                    <p className="text-sm text-violet-100">
                      ConnecTone 서비스 이용을 위한 안내입니다
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="rounded-lg p-2 text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="max-h-[calc(90vh-160px)] space-y-4 overflow-y-auto p-6 text-sm leading-relaxed text-gray-700">
              {isService ? (
                <>
                  <p>
                    ConnecTone은 AI 음악 분석, 프로 피드백, 1:1 레슨을 제공하는
                    음악 성장 플랫폼입니다.
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>회원은 관계 법령과 서비스 이용안내를 준수해야 합니다.</li>
                    <li>유료 서비스의 결제·환불은 각 서비스 화면 안내를 따릅니다.</li>
                    <li>허위 정보 제공, 타인 명의 도용 등은 제한될 수 있습니다.</li>
                  </ul>
                  <Link
                    href="/terms"
                    target="_blank"
                    className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:text-violet-900"
                    onClick={onClose}
                  >
                    전체 이용약관 보기
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </>
              ) : (
                <>
                  <p>
                    회사는 서비스 제공에 필요한 범위에서 개인정보를 수집·이용하며,
                    관련 법령에 따라 안전하게 관리합니다.
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>수집 목적: 회원 식별, 서비스 제공, 고객지원, 결제 처리</li>
                    <li>보관: 관련 법령과 내부 정책에 따른 기간 동안 보관</li>
                    <li>회원은 개인정보 열람·정정·삭제를 요청할 수 있습니다</li>
                  </ul>
                  <Link
                    href="/privacy"
                    target="_blank"
                    className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:text-violet-900"
                    onClick={onClose}
                  >
                    전체 개인정보처리방침 보기
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </>
              )}
            </div>

            <div className="border-t bg-gray-50 px-6 py-4">
              <Button
                onClick={onClose}
                className="w-full bg-violet-600 text-white hover:bg-violet-700"
              >
                확인
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
